import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, MessageSquare, X, Send, Smile } from "lucide-react";
import { PatreonBadge } from "@/components/UserBadge";
import { timeAgo } from "@/lib/time";

const REACTIONS = ["\u2764\ufe0f", "\ud83d\udd25", "\ud83d\ude02", "\ud83d\ude2e", "\ud83d\ude22", "\ud83d\udc4e"];

function CommentRow(props) {
  const { c, currentUser, onDelete, onReact, onReply, depth } = props;
  const [showReply, setShowReply] = useState(false);
  const [showAllReacts, setShowAllReacts] = useState(false);
  const [replyText, setReplyText] = useState("");

  const reactionEntries = Object.entries(c.reactions || {}).filter(function (pair) {
    return pair[1] && pair[1].length > 0;
  });
  const reactedByMe = function (emoji) {
    return currentUser && (c.reactions?.[emoji] || []).indexOf(currentUser.id) !== -1;
  };

  const submitReply = async function () {
    const t = replyText.trim();
    if (!t) return;
    const created = await onReply(c.id, t);
    if (created) {
      setReplyText("");
      setShowReply(false);
    }
  };

  const indent = depth > 0 ? "ml-4 sm:ml-10 border-l-2 border-white/5 pl-3" : "";

  return (
    <div data-testid={`comment-${c.id}`} id={`comment-${c.id}`} className={indent}>
      <div className="flex gap-3 p-3 rounded-xl bg-white/5 border border-white/5">
        <Link to={`/users/${c.user_id}`} className="w-9 h-9 rounded-full bg-gradient-to-br from-[#E50914] to-[#7a0309] flex items-center justify-center font-black uppercase text-sm shrink-0 overflow-hidden">
          {c.user_avatar ? <img src={c.user_avatar} alt="" className="w-full h-full object-cover" /> : (c.user_name || "?")[0]}
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <Link to={`/users/${c.user_id}`} className="font-bold text-sm hover:text-[#E50914] truncate">{c.user_name}</Link>
              {c.user_role === "admin" && <span className="text-[9px] font-black text-[#E50914] uppercase tracking-widest">Admin</span>}
              {c.patreon_tier && <PatreonBadge tier={c.patreon_tier} />}
            </div>
            <span className="text-[10px] text-zinc-500 whitespace-nowrap">{c.created_at ? timeAgo(c.created_at) : ""}</span>
          </div>
          <p className="text-sm text-zinc-300 mt-1 break-words whitespace-pre-wrap">{c.text}</p>

          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            {reactionEntries.map(function (pair) {
              const emoji = pair[0];
              const users = pair[1];
              const mine = reactedByMe(emoji);
              const cls = mine
                ? "bg-[#E50914]/15 border-[#E50914]/50 text-white"
                : "bg-white/5 border-white/10 text-zinc-300 hover:border-white/20";
              return (
                <button
                  key={emoji}
                  onClick={function () { onReact(c.id, emoji); }}
                  data-testid={`comment-react-${c.id}-${emoji}`}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold border transition-colors ${cls}`}
                >
                  <span className="text-sm leading-none">{emoji}</span>
                  <span>{users.length}</span>
                </button>
              );
            })}
            <button
              onClick={function () { setShowAllReacts(!showAllReacts); }}
              data-testid={`comment-react-toggle-${c.id}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border border-white/10 bg-white/5 text-zinc-400 hover:text-white"
            >
              <Smile className="w-3 h-3" />
            </button>
            {showAllReacts && (
              <div className="inline-flex items-center gap-1 px-1.5 py-1 rounded-full bg-black/70 border border-white/15">
                {REACTIONS.map(function (emoji) {
                  return (
                    <button
                      key={emoji}
                      onClick={function () { onReact(c.id, emoji); setShowAllReacts(false); }}
                      data-testid={`comment-pick-${c.id}-${emoji}`}
                      className="text-base leading-none px-1 py-0.5 rounded hover:scale-125 transition-transform"
                    >
                      {emoji}
                    </button>
                  );
                })}
              </div>
            )}
            {depth < 3 && currentUser && (
              <button
                onClick={function () { setShowReply(!showReply); }}
                data-testid={`comment-reply-btn-${c.id}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold text-zinc-400 hover:text-white"
              >
                {showReply
                  ? <span className="inline-flex items-center gap-1"><X className="w-3 h-3" /> Cancel</span>
                  : <span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Reply</span>}
              </button>
            )}
          </div>

          {showReply && (
            <form
              onSubmit={function (e) { e.preventDefault(); submitReply(); }}
              className="flex gap-2 mt-2"
              data-testid={`comment-reply-form-${c.id}`}
            >
              <input
                autoFocus
                value={replyText}
                onChange={function (e) { setReplyText(e.target.value); }}
                placeholder={`Reply to ${c.user_name}...`}
                className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder:text-zinc-500 outline-none focus:border-[#E50914]/50"
              />
              <button
                type="submit"
                disabled={!replyText.trim()}
                className="px-3 py-2 rounded-lg bg-[#E50914] hover:bg-[#F6121D] disabled:opacity-50 text-white text-sm font-bold inline-flex items-center gap-1"
              >
                <Send className="w-3.5 h-3.5" />
              </button>
            </form>
          )}
        </div>
        {currentUser && (currentUser.id === c.user_id || currentUser.role === "admin") && (
          <button
            data-testid={`del-comment-${c.id}`}
            onClick={function () { onDelete(c.id); }}
            className="text-zinc-500 hover:text-[#E50914] self-start"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

// Flatten the comment tree into a linear list with depth annotation, so the render is not recursive.
function flattenTree(roots) {
  const out = [];
  const stack = roots.slice().map(function (r) { return { node: r, depth: 0 }; }).reverse();
  while (stack.length) {
    const cur = stack.pop();
    out.push({ node: cur.node, depth: cur.depth });
    const kids = (cur.node._children || []).slice().reverse();
    for (const k of kids) stack.push({ node: k, depth: cur.depth + 1 });
  }
  return out;
}

export default function CommentList(props) {
  const { comments, currentUser, onDelete, onReact, onReply } = props;

  const flat = useMemo(function () {
    const byId = new Map();
    const sorted = comments.slice().sort(function (a, b) {
      return (a.created_at || "").localeCompare(b.created_at || "");
    });
    for (const c of sorted) byId.set(c.id, Object.assign({}, c, { _children: [] }));
    const roots = [];
    for (const c of sorted) {
      const node = byId.get(c.id);
      if (c.parent_id && byId.has(c.parent_id)) byId.get(c.parent_id)._children.push(node);
      else roots.push(node);
    }
    roots.reverse();
    return flattenTree(roots);
  }, [comments]);

  if (comments.length === 0) return <p className="text-sm text-zinc-500">No comments yet. Be the first.</p>;

  return (
    <div className="space-y-2">
      {flat.map(function (row) {
        return (
          <CommentRow
            key={row.node.id}
            c={row.node}
            depth={row.depth}
            currentUser={currentUser}
            onDelete={onDelete}
            onReact={onReact}
            onReply={onReply}
          />
        );
      })}
    </div>
  );
}
